import { supabase, supabaseAnonKey, safeGetSession, safeRefreshSession } from "./supabaseClient.js";

// Ensure a non-expired session BEFORE writing to push_subscriptions.
// push_subscriptions RLS requires an authenticated request (auth.uid() =
// user_id). On mobile/PWA, getSession() can hand back a cached EXPIRED JWT, so
// the write is RLS-denied and — because every caller swallowed the error — the
// heal silently "succeeded" while the DB row went stale or was never created.
// This is why one player's web sub froze weeks ago and the other had none.
// Mirror the ensureSession()-before-write pattern the presence code already uses.
async function ensureFreshSession() {
  try {
    const { data: { session } } = await safeGetSession();
    const expSoon = session?.expires_at ? (session.expires_at * 1000) < (Date.now() + 30000) : true;
    if (session && !expSoon) return true;
    const { data, error } = await safeRefreshSession();
    return !error && !!data?.session;
  } catch { return false; }
}

// VAPID public key for Web Push — must match the key pair in Supabase secrets
const VAPID_PUBLIC_KEY = "BCUva-G0mFqIpW-3ijBfOzpt7Rbaj-g9tLVF7GsMl9txpLeKm2JIQY-Vkr1w127-bNBleQreb-cLvFH0LTurDko";

// Direct edge function URL — bypass supabase.functions.invoke which silently fails on iOS
const SUPABASE_URL = "https://dxdwnpnavlohkwtxfwgq.supabase.co";
const SEND_PUSH_URL = `${SUPABASE_URL}/functions/v1/send-push`;

// localStorage flag recording that the user has opted into push on THIS device.
// Survives deploys / cache busts (localStorage is not cleared by SW cache
// versioning), so it's the durable signal of intent that lets us silently
// re-subscribe after a service-worker swap drops the live subscription.
const PUSH_OPTIN_KEY = "mfml_push_optin";

export function hasPushOptIn() {
  try { return localStorage.getItem(PUSH_OPTIN_KEY) === "1"; } catch { return false; }
}

export function setPushOptIn(on) {
  try {
    if (on) localStorage.setItem(PUSH_OPTIN_KEY, "1");
    else localStorage.removeItem(PUSH_OPTIN_KEY);
  } catch { /* private mode / storage disabled — non-fatal */ }
}

// Identify the push service from an endpoint URL. Used to dedup stale
// subscriptions on the SAME device when the OS rotates the endpoint:
// for a given (user, push service host) tuple, only the newest endpoint
// should remain.
function pushServiceHost(endpoint) {
  if (!endpoint) return null;
  if (endpoint.includes("fcm.googleapis.com")) return "fcm";
  if (endpoint.includes("web.push.apple.com")) return "apns";
  if (endpoint.includes("mozilla.com") || endpoint.includes("mozaws")) return "mozilla";
  if (endpoint.includes("notify.windows.com")) return "wns";
  // Fall back to URL hostname so different services don't collide
  try { return new URL(endpoint).hostname; } catch { return "other"; }
}

// Delete other push_subscriptions for this user that target the SAME push
// service host but have a different endpoint (the dead, rotated-out endpoint).
async function deleteSameHostExcept(userId, host, keepEndpoint) {
  if (!userId || !host) return;
  // List the user's existing subs and delete the ones whose host matches but
  // endpoint differs. We do it in JS rather than a single SQL CASE expression
  // because Supabase JS doesn't support complex computed WHERE filters.
  const { data: rows, error } = await supabase
    .from("push_subscriptions")
    .select("id, endpoint")
    .eq("user_id", userId);
  if (error || !rows) return;
  const stale = rows.filter(r => r.endpoint !== keepEndpoint && pushServiceHost(r.endpoint) === host);
  if (!stale.length) return;
  const ids = stale.map(r => r.id);
  const { error: delErr } = await supabase.from("push_subscriptions").delete().in("id", ids);
  if (delErr) {
    console.warn("[Push] Failed to delete stale same-host subs:", delErr.message);
  } else {
    console.log(`[Push] Cleaned ${ids.length} stale ${host} sub(s)`);
  }
}

// Convert URL-safe base64 to Uint8Array (required for push subscription)
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Register service worker
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    console.log("[Push] Service workers not supported");
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register("/sw.js");
    console.log("[Push] SW registered:", registration.scope);
    return registration;
  } catch (err) {
    console.error("[Push] SW registration failed:", err);
    return null;
  }
}

// Check if push is supported (also checks standalone PWA on iOS)
export function isPushSupported() {
  // Basic checks
  if (!("serviceWorker" in navigator)) return false;
  if (!("PushManager" in window)) return false;
  if (!("Notification" in window)) return false;
  return true;
}

// Get current push subscription status
export async function getPushSubscription() {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (err) {
    console.error("[Push] Error getting subscription:", err);
    return null;
  }
}

// Subscribe to push notifications
export async function subscribeToPush(userId) {
  if (!isPushSupported()) {
    throw new Error("Push notifications are not supported in this browser");
  }
  if (!userId) {
    throw new Error("User ID required for push subscription");
  }

  // Request notification permission
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission denied");
  }

  try {
    // Ensure SW is registered and ready
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await registerServiceWorker();
    }
    if (!registration) {
      throw new Error("Service worker registration failed");
    }
    await navigator.serviceWorker.ready;

    // Check for existing subscription — reuse if VAPID key matches
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Verify existing subscription uses our current VAPID key
      const existingKey = subscription.options?.applicationServerKey;
      const expectedKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      if (existingKey) {
        const existingArr = new Uint8Array(existingKey);
        const keysMatch = existingArr.length === expectedKey.length &&
          existingArr.every((v, i) => v === expectedKey[i]);
        if (!keysMatch) {
          console.log("[Push] VAPID key changed, resubscribing...");
          await subscription.unsubscribe();
          subscription = null;
        }
      }
    }

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    // Save subscription to Supabase — RLS write, so the JWT must be FRESH.
    // ensurePushSubscription was hardened for exactly this (stale iOS/PWA JWT
    // → silent RLS denial) but the ENABLE path skipped the guard, so flipping
    // the toggle on could silently save nothing.
    await ensureFreshSession();
    const subJson = subscription.toJSON();
    console.log("[Push] Saving subscription for user:", userId);
    console.log("[Push] Endpoint:", subJson.endpoint?.substring(0, 60) + "...");

    const { error } = await supabase.from("push_subscriptions").upsert(
      {
        user_id: userId,
        endpoint: subJson.endpoint,
        p256dh: subJson.keys.p256dh,
        auth: subJson.keys.auth,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "endpoint" }
    );

    if (error) {
      console.error("[Push] Failed to save subscription:", error);
      // If the table doesn't exist, log a clear message
      if (error.code === "42P01" || error.message?.includes("does not exist")) {
        console.error("[Push] push_subscriptions table does not exist! Run the migration SQL.");
      }
      throw error;
    }

    // Clean up stale subs for the SAME user + SAME push service host (= same
    // device, just a rotated endpoint). iOS rotates push subscriptions
    // silently and the upsert above keys on endpoint, so the OLD endpoint
    // row would otherwise stick around forever. send-push then wastes calls
    // and (with the check-unread cooldown design) can swallow reminders when
    // every sub is stale.
    try {
      const host = pushServiceHost(subJson.endpoint);
      if (host) {
        await deleteSameHostExcept(userId, host, subJson.endpoint);
      }
    } catch (cleanupErr) {
      // Non-fatal — main upsert already succeeded
      console.warn("[Push] Stale-sub cleanup failed (non-fatal):", cleanupErr);
    }

    setPushOptIn(true);
    console.log("[Push] Subscription saved successfully");
    return subscription;
  } catch (err) {
    console.error("[Push] Subscription failed:", err);
    throw err;
  }
}

// Auto-heal the push subscription so notifications persist across deploys.
//
// THE BUG THIS FIXES: every deploy ships a new service worker (sw.js, with a
// bumped CACHE_VERSION). On iOS PWAs especially, swapping the active SW can
// silently drop the existing push subscription. The Settings toggle reads its
// on/off state straight from pushManager.getSubscription(), so a dropped
// subscription makes the toggle flip to OFF on its own — even though the user
// never touched it and their notification_preferences rows are untouched.
//
// This runs on every app load. When the device previously opted into push and
// notification permission is still "granted", it silently re-creates the
// subscription (no prompt — permission already exists) and re-saves it to the
// push_subscriptions table. Net effect: push survives every future deploy.
//
// Returns the live PushSubscription (existing or freshly healed), or null when
// the user has not opted in / permission isn't granted / push isn't supported.
export async function ensurePushSubscription(userId) {
  if (!isPushSupported() || !userId) return null;
  // Never prompt here. We only act when permission is already granted.
  if (Notification.permission !== "granted") return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Subscription is alive. Make sure the DB row exists and is fresh — a
      // prior stale-cleanup pass or a failed save could otherwise leave the
      // table without this endpoint, which silently breaks delivery.
      const subJson = subscription.toJSON();
      await ensureFreshSession(); // RLS write — need a valid JWT (see helper)
      let { error } = await supabase.from("push_subscriptions").upsert(
        {
          user_id: userId,
          endpoint: subJson.endpoint,
          p256dh: subJson.keys?.p256dh,
          auth: subJson.keys?.auth,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "endpoint" }
      );
      if (error) {
        // Don't swallow it as success — a failed persist means the row is
        // stale/missing and delivery is broken. Force-refresh and retry once.
        console.warn("[Push] ensure: refresh upsert failed, retrying:", error.message);
        await safeRefreshSession().catch(() => {});
        ({ error } = await supabase.from("push_subscriptions").upsert(
          { user_id: userId, endpoint: subJson.endpoint, p256dh: subJson.keys?.p256dh, auth: subJson.keys?.auth, updated_at: new Date().toISOString() },
          { onConflict: "endpoint" }
        ));
        if (error) console.error("[Push] ensure: upsert still failing after refresh:", error.message);
      }
      setPushOptIn(true); // a live sub means they're opted in
      return subscription;
    }

    // No live subscription. Only auto-resubscribe if the user actually wants
    // push. The localStorage flag is the primary signal; for users who opted
    // in BEFORE this flag existed (or on a device whose flag was cleared by a
    // storage reset), fall back to "is there already a push_subscriptions row
    // for this user?" as a migration heuristic.
    if (!hasPushOptIn()) {
      const { data: existingRows, error } = await supabase
        .from("push_subscriptions")
        .select("id")
        .eq("user_id", userId)
        .limit(1);
      if (error || !existingRows || existingRows.length === 0) return null;
      // They had push before — adopt the opt-in going forward.
      setPushOptIn(true);
    }

    console.log("[Push] Subscription missing but user opted in — auto-healing");
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });

    const subJson = subscription.toJSON();
    await ensureFreshSession(); // RLS write — need a valid JWT (see helper)
    const { error: saveErr } = await supabase.from("push_subscriptions").upsert(
      {
        user_id: userId,
        endpoint: subJson.endpoint,
        p256dh: subJson.keys.p256dh,
        auth: subJson.keys.auth,
        updated_at: new Date().toISOString(),
      },
      { onConflict: "endpoint" }
    );
    if (saveErr) {
      console.error("[Push] Auto-heal save failed:", saveErr.message);
      return subscription; // browser is subscribed even if the DB write lagged
    }

    // Drop the dead endpoint(s) we just replaced on this device.
    try {
      const host = pushServiceHost(subJson.endpoint);
      if (host) await deleteSameHostExcept(userId, host, subJson.endpoint);
    } catch { /* non-fatal */ }

    console.log("[Push] Auto-heal re-subscribe succeeded");
    return subscription;
  } catch (err) {
    console.warn("[Push] ensurePushSubscription failed (non-fatal):", err);
    return null;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush(_userId) {
  try {
    const subscription = await getPushSubscription();
    if (subscription) {
      // Remove from Supabase
      const { error } = await supabase
        .from("push_subscriptions")
        .delete()
        .eq("endpoint", subscription.endpoint);

      if (error) {
        console.error("[Push] Failed to delete subscription from DB:", error);
      }

      // Unsubscribe from browser
      await subscription.unsubscribe();
      console.log("[Push] Unsubscribed successfully");
    }
    // Explicit user opt-out — clear the flag so auto-heal won't re-subscribe.
    setPushOptIn(false);
    return true;
  } catch (err) {
    console.error("[Push] Unsubscribe failed:", err);
    return false;
  }
}

// Send a push notification via direct fetch to Supabase Edge Function
// Uses direct fetch instead of supabase.functions.invoke to avoid silent
// failures on iOS Safari where the SDK POST never reaches the server
// (only the CORS OPTIONS preflight was getting through)
//
// NOTE: The service worker (sw.js) handles suppressing notifications when
// the app is focused — no need to check document.hasFocus() here.
// Every browser push goes through here: refresh the session when the token
// is at or near expiry, send, and on a 401 refresh once more and retry —
// send-push rejects expired JWTs, and 31 pushes were rejected in one evening
// hour on 2026-09-09 while the other player waited for a buzz that never came.
const RETRY_AFTER_5XX_MS = 1500;
export async function sendPushRaw(body) {
  await ensureFreshSession();
  const attempt = async () => {
    const { data: sessionData } = await safeGetSession();
    const accessToken = sessionData?.session?.access_token;
    return fetch(SEND_PUSH_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": supabaseAnonKey,
        ...(accessToken ? { "Authorization": `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(body),
    });
  };
  let response = await attempt();
  if (response.status === 401) {
    console.warn("[Push] send-push 401 — refreshing the session and retrying once");
    await safeRefreshSession().catch(() => {});
    response = await attempt();
  }
  // v400: a gateway 5xx gets one more try. On 2026-10-05 22:25 ET the edge
  // gateway answered a per-message push with 503 in 8 ms (the function never
  // booted) and the other player's only notification for that message was
  // lost. check-unread v17 nets that within minutes; the fast path should
  // not lean on the net for a blip that clears in a second.
  if (response.status >= 500 && response.status < 600) {
    console.warn(`[Push] send-push ${response.status} — retrying once in ${RETRY_AFTER_5XX_MS}ms`);
    await new Promise((resolve) => setTimeout(resolve, RETRY_AFTER_5XX_MS));
    response = await attempt();
  }
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    console.error(`[Push] send-push HTTP ${response.status}:`, errText);
  }
  return response;
}

export async function sendPushNotification(recipientUserId, payload) {
  try {
    const body = {
      title: payload.title || "MFML",
      body: payload.body || "New notification",
      url: payload.url || "/",
    };
    // Target a specific user, or exclude the sender
    if (recipientUserId) body.user_id = recipientUserId;
    if (payload.exclude_user_id) body.exclude_user_id = payload.exclude_user_id;
    // Notification category for per-user preference filtering (dm, channel, story, phone, community)
    if (payload.category) body.category = payload.category;

    console.log("[Push] Sending notification via direct fetch:", body.title);

    // Refresh first when the token is at/near expiry (audit PUSH-6). The
    // bearer used to come from a bare safeGetSession, whose 30s cache never
    // checks expires_at — so the first actions after an overnight iOS resume
    // pushed with an expired token. The old "not required since
    // verify_jwt=false" note is stale: send-push has been hardened since
    // 07-27 and 401s an expired JWT, silently dropping the other player's
    // notification until the background refresh caught up. Every
    // push_subscriptions write in this file already guards this way.
    // One push path (sendPushRaw): fresh session first, one retry on a 401.
    const response = await sendPushRaw(body);

    if (!response.ok) {
      if (response.status === 404) {
        console.error("[Push] send-push Edge Function not deployed! Deploy it to Supabase.");
      }
      return false;
    }

    const data = await response.json().catch(() => ({}));
    console.log("[Push] Notification result:", data);
    return true;
  } catch (err) {
    console.error("[Push] Send failed:", err);
    return false;
  }
}

// Diagnostic function — call from browser console: pushDiagnostics()
// Helps debug push notification issues
export async function pushDiagnostics() {
  const results = {};

  // 1. Browser support
  results.serviceWorkerSupported = "serviceWorker" in navigator;
  results.pushManagerSupported = "PushManager" in window;
  results.notificationSupported = "Notification" in window;
  results.isPushSupported = isPushSupported();

  // 2. Permission state
  results.notificationPermission = Notification.permission;

  // 3. Service worker state
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    results.swRegistered = !!reg;
    results.swState = reg?.active?.state || reg?.installing?.state || reg?.waiting?.state || "none";
    results.swScope = reg?.scope;
  } catch (e) {
    results.swError = e.message;
  }

  // 4. Push subscription
  try {
    const sub = await getPushSubscription();
    results.hasSubscription = !!sub;
    if (sub) {
      results.endpoint = sub.endpoint?.substring(0, 80) + "...";
      results.expirationTime = sub.expirationTime;
    }
  } catch (e) {
    results.subscriptionError = e.message;
  }

  // 5. Standalone mode (PWA)
  results.isStandalone = window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;

  // 6. iOS detection
  results.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  results.isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

  console.log("[Push Diagnostics]", results);
  console.table(results);
  return results;
}

// ── Heal loop ───────────────────────────────────────────────────────────────
//
// ensurePushSubscription is the auto-heal entry point — but it only fires on
// login (via the [user?.id] useEffect in SixGuysRP). For users who keep the
// app open for hours or days (the common case on mobile + desktop), an
// endpoint that silently rotates won't get healed until next login. Push
// then breaks until the user does something that re-mounts the auth effect.
//
// startPushHealLoop layers two extra heal triggers on top:
//
//   1. PERIODIC — every HEAL_INTERVAL_MS (30 min) while the app is open.
//   2. VISIBILITY-CHANGE — when the tab becomes visible after backgrounding
//      (covers "phone was asleep for 6 hours, user returns to the app").
//
// Both heals defer to a min-gap throttle so a backgrounded-then-foregrounded
// tab doesn't fire two heals in quick succession. The heal itself is
// idempotent (upsert by endpoint) so a stray double-call is harmless.
//
// Returns a cleanup function — caller MUST invoke it on unmount / logout to
// stop the interval + remove the visibility listener.
//
// Important: this never clears mfml_push_optin or flips the user's toggle.
// If a heal fails (browser revoked permission, network down, OS quirk), we
// log a warning but leave the opt-in flag set — the user opted in once, only
// the unsubscribeFromPush call (driven by an explicit toggle) can opt them
// back out.
const HEAL_INTERVAL_MS = 30 * 60 * 1000;
const HEAL_MIN_GAP_MS = 60 * 1000;
let lastHealAt = 0;

export function startPushHealLoop(userId) {
  if (!userId) return () => {};
  let stopped = false;

  const maybeHeal = async (reason) => {
    if (stopped) return;
    const now = Date.now();
    if (now - lastHealAt < HEAL_MIN_GAP_MS) return;
    lastHealAt = now;
    try {
      const sub = await ensurePushSubscription(userId);
      if (!sub && hasPushOptIn() && Notification.permission === "granted") {
        // Opted in + permission still granted, but heal couldn't produce a sub.
        // Note this in the console — we DO NOT clear the opt-in flag; the user
        // never asked to be opted out.
        console.warn(`[Push] heal loop (${reason}): no live subscription despite opt-in + granted permission. Browser quirk? Will retry on next interval.`);
      }
    } catch (err) {
      console.warn(`[Push] heal loop (${reason}) threw:`, err?.message || err);
    }
  };

  // Immediate (covers "user just opened the app").
  maybeHeal("initial");

  // Periodic.
  const intervalId = setInterval(() => maybeHeal("interval"), HEAL_INTERVAL_MS);

  // Visibility change — covers "tab was hidden / phone was asleep".
  const onVisibility = () => {
    if (document.visibilityState === "visible") maybeHeal("visibility");
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    stopped = true;
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}

// Listen for subscription changes from the service worker (iOS can rotate subscriptions)
export function listenForSubscriptionChanges(userId) {
  if (!("serviceWorker" in navigator) || !userId) return () => {};

  const handler = async (event) => {
    if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGED" && event.data.subscription) {
      console.log("[Push] Subscription changed by SW, re-saving...");
      const subJson = event.data.subscription;
      const oldEndpoint = event.data.oldEndpoint; // may be null if SW didn't have it
      try {
        const { error } = await supabase.from("push_subscriptions").upsert(
          {
            user_id: userId,
            endpoint: subJson.endpoint,
            p256dh: subJson.keys?.p256dh,
            auth: subJson.keys?.auth,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "endpoint" }
        );
        if (error) {
          console.error("[Push] Failed to re-save changed subscription:", error);
          return;
        }
        // Explicitly drop the rotated-out endpoint if the SW gave it to us.
        if (oldEndpoint && oldEndpoint !== subJson.endpoint) {
          await supabase.from("push_subscriptions").delete().eq("endpoint", oldEndpoint);
        }
        // Belt-and-suspenders: also clear any other same-host stale rows for
        // this user (defensive against multiple rotations).
        try {
          const host = pushServiceHost(subJson.endpoint);
          if (host) await deleteSameHostExcept(userId, host, subJson.endpoint);
        } catch {}
        console.log("[Push] Changed subscription saved successfully");
      } catch (err) {
        console.error("[Push] Error saving changed subscription:", err);
      }
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  // Caller MUST invoke this on logout/unmount — the old version leaked one
  // permanent listener per login, each closed over its own userId (a rotated
  // subscription could be upserted under a PREVIOUS account's id).
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}

// Make diagnostics available globally for console debugging
if (typeof window !== "undefined") {
  window.pushDiagnostics = pushDiagnostics;
}
